import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { Link, useNavigate } from 'react-router-dom';
import { PageShell } from './layout';
import { Skeleton, SkeletonText } from './Skeleton';
import { buildTrackedAuthRedirectUrl } from '../analytics';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { useUserProfile } from '../contexts/UserProfileContext';
import {
  createMondialiRegistration,
  getMondialiCountries,
} from '../api/mondiali';
import {
  MONDIALI_AUTH_ERROR_MESSAGE,
  getFriendlyMondialiRequestError,
  getMondialiUsernameValidationError,
  isMondialiUsernameConflictError,
  normalizeMondialiUsername,
} from '../utils/mondialiRegistration';
import {
  isMondialiArchiveOpen,
  MONDIALI_FINALE_PATH,
} from '../utils/mondialiRouting';
import 'flag-icons/css/flag-icons.min.css';
import styles from '../styles/Mondiali.module.css';

const MONDIALI_SIGNUP_MESSAGE = 'Crea il tuo account su Giochini di Parole per partecipare ad ALMA Giochini Mondiali.';
const MONDIALI_SIGNIN_MESSAGE = 'Accedi al tuo account Giochini di Parole per partecipare ad ALMA Giochini Mondiali.';

const GEOLOCATION_URL = 'https://ipapi.co/json/';
const MONDIALI_STADIUM_IMAGE_URL = `${process.env.PUBLIC_URL || ''}/images/mondiali_bg.jpeg`;

const sanitizeUsernameFromEmail = (email = '') => {
  return email.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12);
};

const normalizeCountryEntry = (country) => {
  const alpha2 = typeof country?.alpha2 === 'string' ? country.alpha2.trim().toUpperCase() : '';
  if (!alpha2) {
    return null;
  }

  return {
    alpha2,
    italian_name: country?.italian_name || country?.country_name_it || country?.english_name || alpha2,
  };
};

const MondialiRegistrationSkeletonForm = () => (
  <div className={styles.registrationSkeleton} aria-hidden="true">
    <div className={styles.field}>
      <Skeleton width="26%" height="0.9rem" borderRadius="6px" />
      <Skeleton width="100%" height="2.8rem" borderRadius="10px" />
    </div>
    <div className={styles.field}>
      <Skeleton width="16%" height="0.9rem" borderRadius="6px" />
      <Skeleton width="100%" height="2.8rem" borderRadius="10px" />
    </div>
    <div className={styles.registrationSkeletonCheckboxGroup}>
      <div className={styles.registrationSkeletonCheckboxRow}>
        <Skeleton width="1.05rem" height="1.05rem" borderRadius="4px" className={styles.registrationSkeletonCheckboxMark} />
        <SkeletonText lines={2} width="96%" />
      </div>
      <div className={styles.registrationSkeletonCheckboxRow}>
        <Skeleton width="1.05rem" height="1.05rem" borderRadius="4px" className={styles.registrationSkeletonCheckboxMark} />
        <SkeletonText lines={2} width="92%" />
      </div>
    </div>
    <Skeleton width="100%" height="3rem" borderRadius="10px" className={styles.registrationSkeletonButton} />
  </div>
);

const MondialiRegistrationPage = () => {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const { user } = useUser();
  const {
    profile,
    isLoading: isProfileLoading,
    updateProfileLocally,
    refreshProfile,
  } = useUserProfile();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const redirectedRef = useRef(false);
  const hasPrefilledUsernameRef = useRef(false);
  const hasUserSelectedCountryRef = useRef(false);
  const countryDropdownRef = useRef(null);

  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [countryAlpha2, setCountryAlpha2] = useState('');
  const [countryError, setCountryError] = useState('');
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [rulesError, setRulesError] = useState('');
  const [almaConsentAccepted, setAlmaConsentAccepted] = useState(false);
  const archiveOpen = isMondialiArchiveOpen(new Date());

  useEffect(() => {
    document.title = 'Registrazione ALMA Giochini Mondiali | Giochini di Parole';
  }, []);

  const buildMondialiAuthRedirectUrl = useCallback((isReturningDevice) => {
    const ctaKind = isReturningDevice ? 'sign_in' : 'sign_up';
    return buildTrackedAuthRedirectUrl({
      cta_kind: ctaKind,
      source_surface: 'mondiali',
      gate_reason: 'mondiali_registration_requires_auth',
      next_path: '/mondiali/registrazione',
      message: isReturningDevice ? MONDIALI_SIGNIN_MESSAGE : MONDIALI_SIGNUP_MESSAGE,
      is_returning_device: isReturningDevice,
      track_gate_viewed: true,
      trigger: 'mondiali_registration_guard',
    });
  }, []);

  const getMondialiAuthToken = useCallback(async () => {
    if (!isSignedIn) {
      return null;
    }
    return getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
  }, [getToken, isSignedIn]);

  useEffect(() => {
    if (archiveOpen) {
      redirectedRef.current = true;
      navigate(MONDIALI_FINALE_PATH, { replace: true });
      return;
    }

    if (archiveOpen) {
      return;
    }

    if (redirectedRef.current || isLoaded || hasEverAuthenticated) {
      return;
    }

    redirectedRef.current = true;
    navigate(buildMondialiAuthRedirectUrl(false), { replace: true });
  }, [archiveOpen, buildMondialiAuthRedirectUrl, hasEverAuthenticated, isLoaded, navigate]);

  useEffect(() => {
    if (archiveOpen) {
      setIsLoading(false);
      return;
    }

    if (!isLoaded || redirectedRef.current || isProfileLoading) {
      return;
    }

    const initialize = async () => {
      if (!isSignedIn) {
        redirectedRef.current = true;
        const authUrl = buildMondialiAuthRedirectUrl(hasEverAuthenticated);
        navigate(authUrl, { replace: true });
        return;
      }

      if (profile?.mondiali?.is_registered) {
        redirectedRef.current = true;
        navigate('/mondiali/home', { replace: true });
        return;
      }

      setError('');

      try {
        const token = await getMondialiAuthToken();
        const countriesPayload = await getMondialiCountries(token);
        const rawCountries = countriesPayload?.countries || [];
        const normalizedCountries = rawCountries
          .map(normalizeCountryEntry)
          .filter(Boolean);
        setCountries(normalizedCountries);
      } catch (_) {
        setError('Non siamo riusciti a caricare la lista dei paesi. Riprova tra poco.');
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [archiveOpen, buildMondialiAuthRedirectUrl, getMondialiAuthToken, hasEverAuthenticated, isLoaded, isProfileLoading, isSignedIn, navigate, profile?.mondiali?.is_registered]);

  useEffect(() => {
    if (hasPrefilledUsernameRef.current || username.trim().length > 0) {
      return;
    }

    const email = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || '';
    const suggestedUsername = sanitizeUsernameFromEmail(email);

    if (suggestedUsername) {
      setUsername(suggestedUsername);
      hasPrefilledUsernameRef.current = true;
    }
  }, [user, username]);

  useEffect(() => {
    if (countries.length === 0 || countryAlpha2 || hasUserSelectedCountryRef.current) {
      return;
    }

    let cancelled = false;

    const initializeCountry = async () => {
      try {
        const response = await fetch(GEOLOCATION_URL);
        const data = await response.json();
        const suggestedAlpha2 = typeof data?.country_code === 'string' ? data.country_code.trim().toUpperCase() : '';
        const countryExists = countries.some((country) => country.alpha2 === suggestedAlpha2);

        if (!cancelled && !hasUserSelectedCountryRef.current && countryExists) {
          setCountryAlpha2(suggestedAlpha2);
          return;
        }
      } catch (_) {
        // Ignore geolocation failures and keep country selection empty.
      }
    };

    initializeCountry();

    return () => {
      cancelled = true;
    };
  }, [countries, countryAlpha2]);

  useEffect(() => {
    if (!isCountryMenuOpen) {
      return;
    }

    const handleClickOutside = (event) => {
      if (!countryDropdownRef.current?.contains(event.target)) {
        setIsCountryMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsCountryMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isCountryMenuOpen]);

  const canSubmit = useMemo(() => {
    return (
      username.trim().length >= 8
      && username.trim().length <= 16
      && /^[a-z0-9]+$/.test(username.trim())
      && countryAlpha2
    );
  }, [username, countryAlpha2]);

  const isPageInitializing = !isLoaded || isProfileLoading || isLoading || redirectedRef.current;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    const nextUsernameError = getMondialiUsernameValidationError(username);
    setUsernameError(nextUsernameError);
    if (nextUsernameError) {
      return;
    }

    const nextCountryError = countryAlpha2 ? '' : 'Seleziona il tuo paese.';
    setCountryError(nextCountryError);
    if (nextCountryError) {
      return;
    }

    if (!rulesAccepted) {
      setRulesError('Devi confermare di aver letto e accettato i regolamenti.');
      return;
    }
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    setRulesError('');

    try {
      const token = await getMondialiAuthToken();
      if (!token) {
        setError(MONDIALI_AUTH_ERROR_MESSAGE);
        return;
      }

      const normalizedUsername = normalizeMondialiUsername(username);

      await createMondialiRegistration({
        username: normalizedUsername,
        country_alpha2: countryAlpha2,
        rules_accepted: rulesAccepted,
        alma_contact_share_accepted: almaConsentAccepted,
        consent_source: 'mondiali_registration_form',
      }, token);

      updateProfileLocally((previousProfile) => ({
        mondiali: {
          ...(previousProfile?.mondiali || {}),
          is_registered: true,
          username: normalizedUsername,
          country_alpha2: countryAlpha2,
        },
      }));
      refreshProfile({ background: true }).catch(() => {});
      navigate('/mondiali/home', { replace: true });
    } catch (submissionError) {
      if (isMondialiUsernameConflictError(submissionError)) {
        setUsernameError('Questo nome utente è già in uso.');
        setError('');
        return;
      }
      setError(getFriendlyMondialiRequestError(submissionError, {
        fallbackMessage: 'Registrazione non completata. Controlla i dati e riprova.',
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUsernameChange = (event) => {
    const nextUsername = event.target.value.toLowerCase();
    setUsername(nextUsername);
    setUsernameError(getMondialiUsernameValidationError(nextUsername));
  };

  const handleCountryChange = (event) => {
    hasUserSelectedCountryRef.current = true;
    const nextCountryAlpha2 = event.target.value;
    setCountryAlpha2(nextCountryAlpha2);
    setCountryError(nextCountryAlpha2 ? '' : 'Seleziona il tuo paese.');
  };

  const selectedCountry = countries.find((country) => country.alpha2 === countryAlpha2) || null;

  return (
    <PageShell onBack={() => navigate('/mondiali')}>
      <div
        className={styles.mondialiFlowBackground}
        style={{ '--mondiali-stadium-image': `url("${MONDIALI_STADIUM_IMAGE_URL}")` }}
      >
        <div className={styles.container}>
          <section className={styles.card}>
            <h1 className={styles.heading}>ALMA Giochini Mondiali</h1>
            <p className={styles.subheading}>
              Scegli nome utente e Paese per entrare nella competizione internazionale a squadre.
            </p>

            {isPageInitializing ? (
              <MondialiRegistrationSkeletonForm />
            ) : (
              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="mondiali-username">Nome utente</label>
                  <input
                    id="mondiali-username"
                    className={`${styles.input} ${usernameError ? styles.inputError : ''}`}
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                    placeholder="es. parola123"
                    autoComplete="off"
                    maxLength={16}
                  />
                  {usernameError && <p className={styles.fieldError}>{usernameError}</p>}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="mondiali-country">Paese</label>
                  <div className={styles.countryDropdown} ref={countryDropdownRef}>
                    <button
                      id="mondiali-country"
                      type="button"
                      className={`${styles.countryTrigger} ${countryError ? styles.inputError : ''}`}
                      onClick={() => setIsCountryMenuOpen((current) => !current)}
                      aria-haspopup="listbox"
                      aria-expanded={isCountryMenuOpen}
                      aria-invalid={Boolean(countryError)}
                      disabled={countries.length === 0}
                    >
                      {selectedCountry ? (
                        <span className={styles.countryOptionLabel}>
                          <span className={`fi fi-${selectedCountry.alpha2.toLowerCase()} ${styles.flag}`} aria-hidden="true" />
                          <span>{selectedCountry.italian_name}</span>
                        </span>
                      ) : (
                        <span>{countries.length > 0 ? 'Scegli il tuo Paese' : 'Nessun Paese disponibile'}</span>
                      )}
                    </button>
                    {isCountryMenuOpen && countries.length > 0 && (
                      <ul className={styles.countryMenu} role="listbox" aria-labelledby="mondiali-country">
                        <li key="__country-placeholder__" role="option" aria-selected={!countryAlpha2}>
                          <button
                            type="button"
                            className={styles.countryMenuItem}
                            onClick={() => {
                              handleCountryChange({ target: { value: '' } });
                              setIsCountryMenuOpen(false);
                            }}
                          >
                            <span>Scegli il tuo paese</span>
                          </button>
                        </li>
                        {countries.map((country) => (
                          <li key={country.alpha2} role="option" aria-selected={country.alpha2 === countryAlpha2}>
                            <button
                              type="button"
                              className={styles.countryMenuItem}
                              onClick={() => {
                                handleCountryChange({ target: { value: country.alpha2 } });
                                setIsCountryMenuOpen(false);
                              }}
                            >
                              <span className={styles.countryOptionLabel}>
                                <span className={`fi fi-${country.alpha2.toLowerCase()} ${styles.flag}`} aria-hidden="true" />
                                <span>{country.italian_name}</span>
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {countryError && <p className={styles.fieldError}>{countryError}</p>}
                </div>

                <div className={styles.checkboxGroup}>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      className={`${styles.checkboxInput} ${rulesError ? styles.checkboxInputError : ''}`}
                      checked={rulesAccepted}
                      onChange={(event) => {
                        setRulesAccepted(event.target.checked);
                        if (event.target.checked) {
                          setRulesError('');
                        }
                      }}
                    />
                    <span className={styles.checkboxText}>
                      Confermo di aver letto e accettato <Link to="/mondiali/regolamenti" target="_blank" rel="noopener noreferrer" className={styles.checkboxLink}>i regolamenti</Link> di ALMA Giochini Mondiali.
                    </span>
                  </label>
                  {rulesError && <p className={styles.fieldError}>{rulesError}</p>}
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      className={styles.checkboxInput}
                      checked={almaConsentAccepted}
                      onChange={(event) => setAlmaConsentAccepted(event.target.checked)}
                    />
                    <span className={styles.checkboxText}>
                      Acconsento alla condivisione dei miei contatti con <a href="https://www.almaedizioni.it/" target="_blank" rel="noreferrer" className={styles.checkboxLink}>ALMA Edizioni</a> per finalità informative e marketing, così da essere idoneo/a ai premi forniti da loro.
                    </span>
                  </label>
                </div>

                {error && (
                  <div className={styles.errorBanner} role="alert">
                    <span className={styles.errorIcon} aria-hidden="true">⚠️</span>
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" className={styles.submitButton}>
                  {isSubmitting ? 'Registrazione in corso...' : 'Conferma registrazione'}
                </button>
              </form>
            )}
          </section>
        </div>
      </div>
    </PageShell>
  );
};

export default MondialiRegistrationPage;
